import { logger } from '@fe/core';
import React from 'react';
import HoldingAnalyzer from '../../../../components/drawing-object/HoldingAnalyzer';
import { Tooltips } from '../../../../components/map-interaction/tooltip/Tooltips';
import { WorkspaceLayout } from '../../../../types/settings';
import { UdViewModel } from '../../../../udView/udview';
import { FeMap } from '../../../components/map/fe-map';
import MaplibreMap from '../../../components/MaplibreMap';
import { useEvent } from '../../../contexts/event-bus';
import WorkspaceItemContext from '../../../contexts/workspace/workspace-item-context';
import { WorkspaceManagementContext } from '../../../contexts/workspace/workspace-management-context';
import { useAppDispatch, useAppSelector } from '../../../hooks/store-hooks';
import { useMapTokens } from '../../../hooks/use-map-settings';
import { useSafeEffect } from '../../../hooks/use-safe-effect';
import { setFocusedWorkspaceItem, setWorkspaceViewLoaded } from '../../../state/slices/workspace-slice';
import { store } from '../../../state/store';
import AtmosphereLayer from '../layers/AtmosphereLayer';
import BrightnessLayer from '../layers/BrightnessLayer';
import DayNightLayer from '../layers/DayNightLayer';
import DistanceBearingLayer from '../layers/DistanceBearingLayer';
import DiversionToggle from '../layers/DiversionLayer';
import DrawingObjectsLayer from '../layers/DrawingObjectLayers';
import FlightLayer from '../layers/flight/FlightLayer';
import LatLngLayer from '../layers/LatLngLayer';
import MapScreenshot from '../layers/MapScreenshot';
import MapViewSync from '../layers/MapState';
import StarsLayer from '../layers/StarsLayer';
import { WeatherLayer } from '../layers/WeatherLayer';
import CustomGeojsonProvider from '../providers/CustomGeojsonProvider';
import MapReadyEventProvider from '../providers/MapReadyEventProvider';
import { NavDataProvider } from '../providers/NavDataProvider';
import { PreviewProvider } from '../providers/PreviewProvider';

export function WorkspaceItem(props: { readonly id: number }) {
    const context = React.useContext(WorkspaceManagementContext);
    const [map, setMap] = React.useState<FeMap | null>(null);
    const [mapReady, setMapReady] = React.useState(false);
    const selectedViewIndex = useAppSelector((state) => state.workspace.selectedId);
    const workspaceContext = React.useContext(WorkspaceItemContext);
    const layout = useAppSelector((x) => x.workspace.layout);
    const mapTokens = useMapTokens();
    const dispatch = useAppDispatch();
    const mapLanguage = useAppSelector((x) => x.settings.mapLanguage);

    useEvent(
        'workspace.view-selected',
        React.useCallback(
            async (e) => {
                if (!map) {
                    return;
                }

                // Check if it's this workspace that the view changed for
                if (e.workspaceId !== props.id) {
                    return;
                }

                // NOTE: Resolve function is passed from the async thunk into here because there's no way from within redux store to access a component in the component tree
                // The reason this needs to be awaited is because we only can switch to new view after the style has loaded. Otherwise the components would
                // render their state to the map then the style would load overwriting those changes
                logger.info("view selected '%s'", e.view.name);
                const view = e.view as UdViewModel;
                map.loadUdView(view.map);
                e.resolveViewLoaded(undefined);
            },
            [map]
        )
    );

    // Update current map tokens
    useSafeEffect(() => {
        map?.setMapTokens(mapTokens);
    }, [map, mapTokens]);

    useSafeEffect(() => {
        map?.setMapLanguage(mapLanguage, navigator.language.split('-')[0]);
    }, [map, mapLanguage]);

    // Load current view on start
    useSafeEffect(() => {
        const view = store.getState().workspace.items[props.id].view;
        map?.loadUdView(view.map);
    }, [map]);

    React.useEffect(() => {
        if (!map) {
            return;
        }

        context.setMap(map, props.id);
        return () => {
            context.setMap(null, props.id);
        };
    }, [map]);

    // As soon as the user touches the map make this workspace item active
    const onMouseDown = React.useCallback(() => {
        if (selectedViewIndex === props.id) {
            return;
        }
        dispatch(setFocusedWorkspaceItem({ id: props.id }));
    }, [selectedViewIndex, dispatch]);

    return (
        <div className="d-flex h-100" onMouseDown={onMouseDown} id={`map-${props.id}`}>
            <div className="pe-auto d-flex flex-column flex-grow-1 position-relative">
                <MaplibreMap
                    className="flex-grow-1"
                    onCreate={setMap}
                    styleLoaded={() => {
                        setMapReady(true);
                        dispatch(setWorkspaceViewLoaded({ workspaceId: workspaceContext.id }));
                    }}>
                    {mapReady && (
                        <>
                            <MapViewSync />
                            <LatLngLayer />
                            <DistanceBearingLayer />
                            <AtmosphereLayer />
                            <StarsLayer />
                            <NavDataProvider />
                            <PreviewProvider />
                            <CustomGeojsonProvider />
                            <DrawingObjectsLayer />
                            <WeatherLayer />
                            <BrightnessLayer />
                            <DayNightLayer />
                            <FlightLayer />
                            <DiversionToggle />
                            <MapScreenshot />
                            <Tooltips />

                            {/*This needs to be last, please and thank you.*/}
                            <MapReadyEventProvider />
                        </>
                    )}
                </MaplibreMap>
                <HoldingAnalyzer />
                {/* Active map border */}
                {layout !== WorkspaceLayout.single && selectedViewIndex === props.id && (
                    <div
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            boxShadow: 'inset 0 0 0 3px rgb(127, 127, 255)',
                            pointerEvents: 'none',
                            zIndex: 'auto'
                        }}
                    />
                )}
            </div>
        </div>
    );
}
